import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { db } from '../../services/firebase/db';
import { useAuth } from '../../hooks/useAuth';
import { collection, query, orderBy, onSnapshot, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Button,
  Badge,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  notificationList: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  notificationItem: {
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  unreadNotification: {
    backgroundColor: theme.palette.action.selected,
  },
  expiryNotification: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  wasteNotification: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
  },
  systemNotification: {
    borderLeft: `4px solid ${theme.palette.info.main}`,
  },
  notificationContent: {
    display: 'flex',
    alignItems: 'center',
  },
  notificationIcon: {
    marginRight: theme.spacing(2),
  },
  notificationDate: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

export function NotificationSystem({ open, message, severity, onClose }) {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
    >
      <Alert 
        onClose={onClose} 
        severity={severity}
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}

NotificationSystem.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NotificationSystem;

export const NotificationSystemComponent = () => {
  const classes = useStyles();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;

    const notificationsQuery = query(
      collection(db, 'notifications'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNotifications(notificationsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
        setError('Error fetching notifications');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { read: true });
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Error updating notification');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Error deleting notification');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const updatePromises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), { read: true })
      );
      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Error updating notifications');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'expiry':
        return <WarningIcon color="warning" />;
      case 'waste':
        return <DeleteIcon color="error" />;
      case 'system':
        return <InfoIcon color="info" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationClass = (type) => {
    switch (type) {
      case 'expiry':
        return classes.expiryNotification;
      case 'waste':
        return classes.wasteNotification;
      case 'system':
        return classes.systemNotification;
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2">
          Notifications
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }}>
              <NotificationsIcon />
            </Badge>
          )}
        </Typography>
        {unreadCount > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleMarkAllAsRead}
            startIcon={<CheckCircleIcon />}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <List className={classes.notificationList}>
        {notifications.length === 0 ? (
          <ListItem>
            <ListItemText primary="No notifications" />
          </ListItem>
        ) : (
          notifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <ListItem
                className={`${classes.notificationItem} ${
                  !notification.read ? classes.unreadNotification : ''
                } ${getNotificationClass(notification.type)}`}
                onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                button
              >
                <ListItemIcon className={classes.notificationIcon}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={notification.message}
                  secondary={
                    <Typography className={classes.notificationDate}>
                      {new Date(notification.date).toLocaleString()}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Delete notification">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(notification.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))
        )}
      </List>
    </Box>
  );
}; 